import { z } from 'zod';
// Widgets are tools Via calls to show something rich in the chat. The browser renders each one inline where it
// was called, from these schemas. Interactive widgets wait on the user through tool approval.
const choiceSchema = z.object({
    label: z.string().describe('The choice in a few words'),
    description: z
        .string()
        .optional()
        .describe('What choosing it means, such as what it counts and how many conversations match'),
    recommended: z
        .boolean()
        .optional()
        .describe('True on the one choice you would go with yourself. The card marks it Recommended, and it is what you do when the user leaves the question unanswered. Never on more than one choice of a question')
});
const questionSchema = z.object({
    id: z.string().describe('Short unique id for the question, such as "q1"'),
    header: z.string().describe('One or two words naming what it decides, such as "Dashboard" or "Booking"'),
    question: z
        .string()
        .describe('The question alone, ending with a question mark. What you would assume goes on the recommended choice, not in these words'),
    options: z.array(choiceSchema).min(2).max(4).describe('Your recommended choice first, marked recommended'),
    multiSelect: z.boolean().optional().describe('Let the user pick more than one choice')
});
// A plan's question can come with a single choice: what the plan assumed, beside the box for the user's own answer.
const planQuestionSchema = questionSchema.extend({
    options: z
        .array(choiceSchema)
        .min(1)
        .max(4)
        .describe('What the plan assumed first, marked recommended, then the alternatives. One choice is enough when the only alternative is a value the user would type, such as an address')
});
const planStepSchema = z.object({
    id: z.string().describe('Short unique id for the step, such as "s1"'),
    title: z.string().describe('What this step does, in a few words the user can scan'),
    detail: z.string().optional().describe('One or two sentences of specifics: which node, what text, why'),
    section: z
        .string()
        .optional()
        .describe('The part of the work this step belongs to, in a few words, such as "Conversation flow" or "Actions". Give every step one when the plan has more than six steps, grouping them into two to five parts in the order you would build them; leave it out of shorter plans')
});
export const proposePlanToolSchema = {
    name: 'propose_plan',
    description: 'Show the user a plan and wait for them to approve it before you build anything. Call it before any change to an agent, however small, and before building or redesigning anything else that takes more than one change. The user can untick steps, approve (answering your questions as they do), or ask for changes with a note. A plan of more than six steps is shown a part at a time, one part per section the steps name, so the user can drop a whole part or leave a note on it. Only carry out the steps the result says were approved, following any answers and notes that came with the approval. If they ask for changes, revise and propose again.',
    inputSchema: z.object({
        title: z.string().describe('What the plan achieves, as a short heading'),
        summary: z.string().describe('One or two sentences on the approach, and anything you assumed'),
        steps: z.array(planStepSchema).min(1),
        questions: z
            .array(planQuestionSchema)
            .optional()
            .describe('Open questions whose answers would change the plan, two at most counting any you asked with ask_user, and never one you have already asked. Each comes with its choices, and the user answers them one at a time as they approve; a question they leave goes with its recommended choice, which is what the summary assumed')
    }),
    outputSchema: z.object({
        approvedSteps: z.array(planStepSchema.pick({ id: true, title: true })),
        skippedSteps: z.array(z.string()).describe('Ids of steps the user unticked; do not carry these out'),
        answers: z
            .string()
            .optional()
            .describe("The user's answers to your questions, each written as the question and then what they chose, and their notes on parts of the plan, each written as Part: note, given with their approval. Build with them; don't ask again")
    })
};
export const askUserToolSchema = {
    name: 'ask_user',
    description: "Ask the user 1–3 multiple-choice questions and wait for their answers. Use it when what they mean can't be settled from their words or their data, such as which dashboard a chart goes on, what kind of chart they want, or what counts as a booking. Give 2–4 concrete choices each, taken from what you found and said in plain words (\"The booking was confirmed (book_meeting): 23 calls\"), with the one you'd pick first and marked recommended. The card shows the questions one at a time. The user can also write their own answer. Don't ask what you can look up yourself, and don't use it to confirm a change: deletes and other changes past the draft already show the user a confirmation card. Before a build or a change, ask no more than two questions in all, here and in the plan together.",
    inputSchema: z.object({ questions: z.array(questionSchema).min(1).max(3) }),
    outputSchema: z.object({
        answers: z.array(z.object({
            questionId: z.string(),
            selected: z.array(z.string()).describe('Labels of the choices the user picked'),
            other: z.string().optional().describe('What the user wrote instead, or as well')
        }))
    })
};
//# sourceMappingURL=widget-schemas.js.map